import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { emailOtp } from '@/lib/auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useTranslation } from 'react-i18next'
import { DashboardMockup } from '@/components/auth/DashboardMockup'

export const Route = createFileRoute('/reset-password')({
  component: ResetPasswordPage,
  validateSearch: (search: Record<string, unknown>) => ({
    email: (search.email as string) || '',
  }),
})

function ResetPasswordPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { email: initialEmail } = Route.useSearch()

  const [email, setEmail] = useState(initialEmail)
  const [otp, setOtp] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (password !== confirmPassword) {
      setError(t('auth.resetPassword.errorMismatch'))
      return
    }

    if (password.length < 8) {
      setError(t('auth.resetPassword.errorLength'))
      return
    }

    if (otp.length !== 6) {
      setError(t('auth.resetPassword.errorCode'))
      return
    }

    setLoading(true)

    try {
      const result = await emailOtp.resetPassword({
        email,
        otp,
        password,
      })

      if (result.error) {
        setError(result.error.message || t('auth.resetPassword.errorDefault'))
      } else {
        setSuccess(true)
        setTimeout(() => {
          navigate({ to: '/login' })
        }, 3000)
      }
    } catch (err) {
      setError(t('auth.resetPassword.errorDefault'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen">
      {/* Left side - Form */}
      <div className="flex w-full flex-col justify-center bg-[#0a0a0f] px-8 py-12 lg:w-1/2 lg:px-16">
        <div className="mx-auto w-full max-w-md">
          {/* Logo */}
          <div className="mb-10">
            <h1 className="text-3xl font-bold text-white">
              <img src="/Zadano/Zadano_Logo_Full_Dark.svg" alt="Zadano.app" className="h-8" />
            </h1>
            <p className="mt-2 text-gray-400">{t('auth.resetPassword.newPasswordTitle')}</p>
          </div>

          {success ? (
            <div className="rounded-lg bg-green-500/10 p-6 text-center">
              <div className="mb-4 text-4xl">🎉</div>
              <h2 className="mb-2 text-xl font-semibold text-white">
                {t('auth.resetPassword.successResetTitle')}
              </h2>
              <p className="text-gray-400">{t('auth.resetPassword.successResetDesc')}</p>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-4 rounded-lg bg-red-500/10 p-3 text-sm text-red-500">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm text-gray-400">
                    {t('auth.email')}
                  </Label>
                  <div className="relative">
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full rounded-none border-0 border-b-2 border-gray-700 bg-transparent pb-3 text-white placeholder-gray-500 shadow-none outline-none transition-colors focus:border-amber-500 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                      placeholder="twoj@email.com"
                      required
                    />
                    <span className="absolute right-0 top-1/2 -translate-y-1/2 text-amber-500">
                      @
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="otp" className="text-sm text-gray-400">
                    {t('auth.resetPassword.codeLabel')}
                  </Label>
                  <Input
                    id="otp"
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-full rounded-none border-0 border-b-2 border-gray-700 bg-transparent pb-3 text-center font-mono text-2xl tracking-widest text-white placeholder-gray-500 shadow-none outline-none transition-colors focus:border-amber-500 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                    placeholder="000000"
                    maxLength={6}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm text-gray-400">
                    {t('auth.resetPassword.newPasswordLabel')}
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full rounded-none border-0 border-b-2 border-gray-700 bg-transparent pb-3 text-white placeholder-gray-500 shadow-none outline-none transition-colors focus:border-amber-500 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                      placeholder={t('auth.resetPassword.tip1')}
                      required
                      minLength={8}
                    />
                    <span className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-500">
                      🔐
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-sm text-gray-400">
                    {t('auth.resetPassword.confirmPasswordLabel')}
                  </Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full rounded-none border-0 border-b-2 border-gray-700 bg-transparent pb-3 text-white placeholder-gray-500 shadow-none outline-none transition-colors focus:border-amber-500 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                      placeholder={t('auth.resetPassword.confirmPasswordLabel')}
                      required
                      minLength={8}
                    />
                    <span className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-500">
                      🔐
                    </span>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-full bg-amber-500 py-6 font-medium text-black hover:bg-amber-400"
                >
                  {loading
                    ? t('auth.resetPassword.resetting')
                    : t('auth.resetPassword.resetButton')}
                </Button>

                <div className="mt-6 text-center">
                  <Link to="/forgot-password" className="text-sm text-amber-500 hover:underline">
                    {t('auth.resetPassword.resendCode')}
                  </Link>
                </div>

                <p className="mt-4 text-center text-gray-400">
                  {t('auth.resetPassword.rememberPassword')}{' '}
                  <Link to="/login" className="font-medium text-amber-500 hover:underline">
                    {t('auth.resetPassword.backToLogin')}
                  </Link>
                </p>
              </form>
            </>
          )}
        </div>
      </div>

      {/* Right side - Marketing */}
      <div className="hidden flex-col justify-center bg-[#0d0d12] px-16 lg:flex lg:w-1/2">
        <div className="mb-8 max-w-lg">
          <div className="max-w-lg">
            <h2 className="text-5xl font-bold leading-tight text-white">
              {t('auth.resetPassword.newPasswordSubtitle')}
            </h2>
            <p className="mt-6 text-lg text-gray-400">{t('auth.resetPassword.newPasswordDesc')}</p>

            {/* Tips */}
            <div className="mt-12 rounded-xl border border-gray-700 bg-gray-800/50 p-6">
              <h3 className="mb-4 font-semibold text-white">
                💡 {t('auth.resetPassword.tipsTitle')}
              </h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>✓ {t('auth.resetPassword.tip1')}</li>
                <li>✓ {t('auth.resetPassword.tip2')}</li>
                <li>✓ {t('auth.resetPassword.tip3')}</li>
                <li>✓ {t('auth.resetPassword.tip4')}</li>
              </ul>
            </div>
          </div>

          {/* App Preview - Mockup */}
          <div className="mt-12 h-[300px] w-full overflow-hidden">
            <div className="w-[200%] origin-top-left scale-50 transform">
              <DashboardMockup />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
